"use client";

import React, {
   useRef,
   useEffect,
   useState,
   forwardRef,
   useImperativeHandle,
   useCallback
} from "react";
import dynamic from "next/dynamic";
import { useTheme } from "next-themes";
import { loader } from "@monaco-editor/react";

// Ensure Monaco Editor loads from the local Public folder, not CDN
if (typeof window !== "undefined") {
   loader.config({ paths: { vs: "/monaco/vs" } });
}

type JsonValue = object | string | number | boolean | null;

interface JsonEditorProps {
   value: string | JsonValue;
   onChange?: (value: JsonValue) => void;
   label?: string;
   height?: string;
   readOnly?: boolean;
   resizable?: boolean;
   minHeight?: string;
   maxHeight?: string;
   expandAll?: boolean;
}

export interface JsonEditorRef {
   getValue: () => JsonValue;
   isValid: () => boolean;
   getError: () => string | null;
   formatDocument?: () => void;
}

const Editor = dynamic(() => import("@monaco-editor/react"), {
   ssr: false,
   loading: () => (
      <div className="border border-input rounded-md p-4 font-mono text-sm bg-muted animate-pulse">
         Loading editor...
      </div>
   )
});

export const JsonEditor = forwardRef<JsonEditorRef, JsonEditorProps>(
   (
      {
         value,
         onChange,
         label,
         height = "400px",
         readOnly = false,
         resizable = true,
         minHeight = "100px",
         maxHeight = "100%",
         expandAll = false
      },
      ref
   ): React.ReactElement => {
      const { resolvedTheme } = useTheme();
      const editorRef = useRef<any>(null);
      const [isClient, setIsClient] = useState(false);
      const [editorValue, setEditorValue] = useState<string>("");
      const [isValidJson, setIsValidJson] = useState(true);
      const [jsonError, setJsonError] = useState<string | null>(null);

      useEffect(() => {
         setIsClient(true);
      }, []);

      const editorTheme = isClient && resolvedTheme === "dark" ? "vs-dark" : "light";

      const convertBacktickToJsonString = useCallback((text: string): string => {
         // Tokenize JSON-with-backticks using a state machine. This avoids the
         // regex pitfalls where backticks inside double-quoted strings (or
         // escaped inside backticks) get mis-matched, which produced the
         // "Expected ',' or '}' after property value" parse error on save.
         let result = "";
         let i = 0;
         const len = text.length;

         while (i < len) {
            const char = text[i];

            // Copy double-quoted JSON strings verbatim, honoring backslash escapes
            if (char === '"') {
               result += char;
               i++;
               while (i < len) {
                  const c = text[i];
                  if (c === "\\" && i + 1 < len) {
                     result += c + text[i + 1];
                     i += 2;
                     continue;
                  }
                  result += c;
                  i++;
                  if (c === '"') break;
               }
               continue;
            }

            // Collect backtick string content and re-emit as a JSON string
            if (char === "`") {
               i++;
               let content = "";
               while (i < len) {
                  const c = text[i];
                  if (c === "\\" && i + 1 < len && text[i + 1] === "`") {
                     content += "`";
                     i += 2;
                     continue;
                  }
                  if (c === "`") {
                     i++;
                     break;
                  }
                  content += c;
                  i++;
               }
               result += JSON.stringify(content);
               continue;
            }

            result += char;
            i++;
         }

         return result;
      }, []);

      const shouldUseBacktick = useCallback((str: string): boolean => {
         return (
            str.includes("\n") &&
            !str.startsWith("{") &&
            !str.startsWith("[") &&
            !str.trim().startsWith("`")
         );
      }, []);

      const stringifyWithBackticks = useCallback(
         (obj: any, indent: number = 0): string => {
            const indentStr = " ".repeat(indent);
            const nextIndent = indent + 2;

            if (typeof obj === "string") {
               if (shouldUseBacktick(obj)) {
                  const escapedForBacktick = obj.replace(/`/g, "\\`");
                  return `\`${escapedForBacktick}\``;
               }
               return JSON.stringify(obj);
            }

            if (Array.isArray(obj)) {
               if (obj.length === 0) return "[]";
               const items = obj
                  .map((item) => {
                     const itemStr = stringifyWithBackticks(item, nextIndent);
                     return `${" ".repeat(nextIndent)}${itemStr}`;
                  })
                  .join(",\n");
               return `[\n${items}\n${indentStr}]`;
            }

            if (obj && typeof obj === "object") {
               const keys = Object.keys(obj);
               if (keys.length === 0) return "{}";
               const items = keys
                  .map((key) => {
                     const value = obj[key];
                     const valueStr = stringifyWithBackticks(value, nextIndent);
                     return `${" ".repeat(nextIndent)}"${key}": ${valueStr}`;
                  })
                  .join(",\n");
               return `{\n${items}\n${indentStr}}`;
            }

            return JSON.stringify(obj);
         },
         [shouldUseBacktick]
      );

      const parseJsonValue = useCallback(
         (val: string | JsonValue): string => {
            try {
               if (typeof val === "string") {
                  const backtickConverted = convertBacktickToJsonString(val);
                  try {
                     const parsed = JSON.parse(backtickConverted);
                     return stringifyWithBackticks(parsed);
                  } catch {
                     return backtickConverted;
                  }
               }
               if (val && typeof val === "object") {
                  return stringifyWithBackticks(val);
               }
               return JSON.stringify(val, null, 2);
            } catch {
               return typeof val === "string" ? val : JSON.stringify(val, null, 2);
            }
         },
         [convertBacktickToJsonString, stringifyWithBackticks]
      );

      const validateJson = useCallback(
         (text: string): boolean => {
            try {
               const backtickConverted = convertBacktickToJsonString(text);
               JSON.parse(backtickConverted);
               setIsValidJson(true);
               setJsonError(null);
               return true;
            } catch (error) {
               setIsValidJson(false);
               setJsonError(error instanceof Error ? error.message : "Invalid JSON");
               return false;
            }
         },
         [convertBacktickToJsonString]
      );

      useEffect(() => {
         setIsClient(true);
      }, []);

      useEffect(() => {
         if (!isClient) return;
         const newValue = parseJsonValue(value);
         setEditorValue(newValue);
         validateJson(newValue);
         // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [value, isClient]);

      const handleEditorChange = useCallback(
         (newValue: string | undefined) => {
            if (!newValue) return;
            setEditorValue(newValue);
            const isValid = validateJson(newValue);
            if (isValid && onChange) {
               try {
                  const backtickConverted = convertBacktickToJsonString(newValue);
                  const parsed = JSON.parse(backtickConverted);
                  onChange(parsed);
               } catch {
                  // Error already handled by validateJson
               }
            }
         },
         [onChange, validateJson, convertBacktickToJsonString]
      );

      const handleEditorDidMount = useCallback(
         (editor: any, monaco: any) => {
            editorRef.current = editor;

            // Disable Monaco's built-in JSON validation to allow backtick format
            // We'll use our own custom validation instead (shown in UI)
            monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
               validate: false,
               schemas: [],
               allowComments: false,
               trailingCommas: "ignore"
            });

            // Clear any existing error markers and prevent new ones for backticks
            const model = editor.getModel();
            if (model) {
               // Clear all existing markers
               monaco.editor.setModelMarkers(model, "json", []);

               // Listen for marker changes and clear backtick-related errors immediately
               const disposable = monaco.editor.onDidChangeMarkers((uris: any[]) => {
                  if (uris.includes(model.uri)) {
                     const markers = monaco.editor.getModelMarkers({
                        resource: model.uri
                     });
                     const text = model.getValue();

                     // Check if text contains backtick patterns
                     const hasBacktickPattern =
                        /"value"\s*:\s*`/.test(text) ||
                        /^`/.test(text.trim()) ||
                        /"([^"]+)":\s*`/.test(text);

                     if (hasBacktickPattern) {
                        // Filter out markers on lines with backticks
                        const filteredMarkers = markers.filter((marker: any) => {
                           const lineText = model.getLineContent(marker.startLineNumber);
                           return !lineText.includes("`");
                        });

                        // Update markers immediately
                        monaco.editor.setModelMarkers(model, "json", filteredMarkers);
                     }
                  }
               });

               // Store disposable for cleanup if needed
               editor.onDidDispose(() => {
                  disposable.dispose();
               });
            }

            if (expandAll) {
               editor.getAction("editor.unfoldAll")?.run();
            }

            editor.addAction({
               id: "format-document",
               label: "Format Document",
               keybindings: [
                  monaco.KeyMod.Shift | monaco.KeyMod.Alt | monaco.KeyCode.KeyF
               ],
               contextMenuGroupId: "1_modification",
               contextMenuOrder: 1.5,
               run: function (ed: any) {
                  ed.getAction("editor.action.formatDocument")?.run();
               }
            });
         },
         [expandAll, convertBacktickToJsonString]
      );

      const formatDocument = useCallback(() => {
         if (editorRef.current) {
            editorRef.current.getAction("editor.action.formatDocument")?.run();
         }
      }, []);

      const readCurrentText = useCallback((): string => {
         return editorRef.current ? editorRef.current.getValue() : editorValue;
      }, [editorValue]);

      useImperativeHandle(ref, () => ({
         getValue: () => {
            try {
               const backtickConverted = convertBacktickToJsonString(readCurrentText());
               return JSON.parse(backtickConverted);
            } catch (error) {
               console.error("Error getting value from editor:", error);
               return null;
            }
         },
         isValid: () => {
            try {
               JSON.parse(convertBacktickToJsonString(readCurrentText()));
               return true;
            } catch {
               return false;
            }
         },
         getError: () => {
            try {
               JSON.parse(convertBacktickToJsonString(readCurrentText()));
               return null;
            } catch (error) {
               return error instanceof Error ? error.message : "Invalid JSON";
            }
         },
         formatDocument
      }));

      if (!isClient) {
         return (
            <div
               className={`w-full ${height === "100%" ? "h-full flex flex-col min-h-0" : ""}`}
            >
               {label && (
                  <label className="block text-sm font-medium mb-1 shrink-0">
                     {label}
                  </label>
               )}
               <div
                  className={`border border-input rounded-md overflow-hidden p-4 font-mono text-sm bg-muted ${height === "100%" ? "flex-1 min-h-0" : ""}`}
                  style={{
                     height: height === "100%" ? undefined : height,
                     minHeight,
                     maxHeight
                  }}
               >
                  {typeof value === "string" ? value : JSON.stringify(value, null, 2)}
               </div>
            </div>
         );
      }

      return (
         <div
            className={`w-full ${height === "100%" ? "h-full flex flex-col min-h-0" : ""}`}
         >
            {label && (
               <label className="block text-sm font-medium mb-1 shrink-0">{label}</label>
            )}
            <div
               className={`relative ${height === "100%" ? "flex-1 flex flex-col min-h-0" : ""}`}
            >
               <div
                  className={`border border-input rounded-md overflow-hidden ${height === "100%" ? "flex-1 min-h-0" : ""}`}
                  style={{
                     height: height === "100%" ? undefined : height,
                     minHeight,
                     maxHeight,
                     resize: resizable && height !== "100%" ? "vertical" : "none"
                  }}
               >
                  <Editor
                     height="100%"
                     language="json"
                     theme={editorTheme}
                     value={editorValue}
                     onChange={handleEditorChange}
                     beforeMount={(monaco) => {
                        // Disable JSON validation before editor mounts
                        monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
                           validate: false,
                           schemas: [],
                           allowComments: false,
                           trailingCommas: "ignore"
                        });
                     }}
                     onMount={handleEditorDidMount}
                     options={{
                        readOnly,
                        minimap: { enabled: true },
                        fontSize: 14,
                        lineNumbers: "on",
                        roundedSelection: false,
                        scrollBeyondLastLine: false,
                        automaticLayout: true,
                        tabSize: 2,
                        insertSpaces: true,
                        wordWrap: "on",
                        formatOnPaste: true,
                        formatOnType: true,
                        folding: true,
                        foldingStrategy: "indentation",
                        showFoldingControls: "always",
                        matchBrackets: "always",
                        autoIndent: "full",
                        suggestOnTriggerCharacters: true,
                        acceptSuggestionOnEnter: "on",
                        tabCompletion: "on",
                        wordBasedSuggestions: "allDocuments",
                        renderValidationDecorations: "off",
                        renderWhitespace: "selection",
                        cursorBlinking: "smooth",
                        smoothScrolling: true,
                        padding: { top: 8, bottom: 8 },
                        scrollbar: {
                           vertical: "auto",
                           horizontal: "auto",
                           useShadows: false,
                           verticalHasArrows: false,
                           horizontalHasArrows: false
                        }
                     }}
                  />
               </div>
               {!isValidJson && jsonError && (
                  <div className="mt-2 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md p-2">
                     <strong>JSON Error:</strong> {jsonError}
                  </div>
               )}
               {!readOnly && isClient && (
                  <div className="mt-2 space-y-1">
                     <div className="text-xs text-muted-foreground">
                        Tip: Press{" "}
                        <kbd className="px-1.5 py-0.5 bg-muted border border-border rounded text-xs font-mono">
                           {typeof window !== "undefined" &&
                           navigator.platform.includes("Mac")
                              ? "Cmd"
                              : "Ctrl"}
                        </kbd>
                        {" + "}
                        <kbd className="px-1.5 py-0.5 bg-muted border border-border rounded text-xs font-mono">
                           Shift
                        </kbd>
                        {" + "}
                        <kbd className="px-1.5 py-0.5 bg-muted border border-border rounded text-xs font-mono">
                           F
                        </kbd>{" "}
                        to format document
                     </div>
                     <div className="text-xs text-muted-foreground">
                        💡 Tip: Use backticks (`) for multiline text strings to avoid
                        escape characters
                     </div>
                  </div>
               )}
            </div>
         </div>
      );
   }
);

JsonEditor.displayName = "JsonEditor";
